import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { formatDateSr } from "@normax-performance/shared-format";
import type { ReportTable, ReportTableColumn } from "@normax-performance/shared-types";
import { Badge, SortableColumnHeader, useSortableRows } from "../../../shared-ui/primitives";
import { useLocale } from "../../../i18n/LocaleProvider";
import { formatAnalyticsMinutes, formatAnalyticsPercent, formatAnalyticsTrend } from "../../../lib/format";

export interface ReportSectionTableProps {
  table: ReportTable;
  /** Show this table's own title as a panel heading — pass `true` only when
   * a section holds more than one table (premium-presentation-polish: a
   * single-table section already states its title on the enclosing
   * `<section>`'s own `<h2>`; repeating it verbatim on the table too read as
   * a doubled heading). Defaults to `true` so a table used standalone
   * (outside `ReportDetailPage`'s own multi-table grouping) still labels
   * itself. */
  showTitle?: boolean;
}

type TableRow = Record<string, string | number | null>;

const ZONE_TONE: Record<string, "danger" | "success" | "neutral"> = { below: "danger", above: "success", neutral: "neutral" };

/** Fallback classification for the one table with a `performance` column
 * but no sibling `zone` column (Worker report's daily history) — fixed
 * thresholds per the approved presentation contract, never recomputes
 * Method C itself (the number was already computed by the backend; this
 * only buckets an already-final number for color). */
function classifyPerformanceValue(value: number): "danger" | "success" | "neutral" {
  if (value > 100) return "success";
  if (value < 90) return "danger";
  return "neutral";
}

function alignClassName(align: ReportTableColumn["align"]): string | undefined {
  if (align === "right") return "nx-report-table__num";
  if (align === "center") return "nx-report-table__center";
  return undefined;
}

// `titleKey`/`labelKey`/zone/status values all arrive as plain `string` from
// the backend's `ReportTable`/`ReportTableColumn` DTOs (data-driven keys, not
// compile-time literal unions), so `t()` can't type-check them against the
// generated key union — same fix as `DailyEntryHistoryModal.tsx`'s
// `fieldLabel`/`formatChangeValue`: cast `as never` and pass the raw value as
// `defaultValue` so a genuinely missing key still renders something.
function translateDynamic(t: TFunction<"reports">, key: string, fallback: string): string {
  return t(key as never, { defaultValue: fallback });
}

/** The one generic, sortable table renderer every report type's sections
 * use — driven entirely by `ReportTable`'s own `columns`/`format` metadata,
 * never a per-report bespoke table component (task spec §9/§27: reuse
 * `SortableColumnHeader`/`useSortableRows`, no BI-builder feel).
 *
 * premium-presentation-polish: each table now renders inside its own
 * `.nx-report-panel` (visual containment, so a multi-table section like
 * "Raščlanjivanje"/"Rangiranje" reads as distinct sub-panels, never one
 * long blended table — task spec §11). */
export function ReportSectionTable({ table, showTitle = true }: ReportSectionTableProps) {
  const { t } = useTranslation("reports");
  const { intlLocale } = useLocale();

  const columnAccessors = Object.fromEntries(
    table.columns.map((col) => [
      col.key,
      (row: TableRow) => {
        const value = row[col.key] ?? null;
        if (value === null) return null;
        if (col.format === "percent" || col.format === "minutes" || col.format === "minutesFraction" || col.format === "count" || col.format === "trend") return Number(value);
        return String(value);
      },
    ]),
  );

  const { sorted, sortKey, direction, requestSort } = useSortableRows(
    table.rows as TableRow[],
    columnAccessors,
    { key: table.defaultSortKey ?? table.columns[0]!.key, direction: table.defaultSortDirection ?? "asc" },
  );

  function renderCell(row: TableRow, value: string | number | null, format: ReportTableColumn["format"]): string | ReactElement {
    if (value === null) return "—";
    switch (format) {
      case "percent": {
        const formatted = formatAnalyticsPercent(String(value), intlLocale);
        // The number itself carries the semantic color (not just the zone
        // badge) — premium-presentation-polish, performance-number-color
        // addendum. Prefer the row's own backend-computed `zone` (present
        // on every table that also shows a `zone`-format column — worker/
        // product/operation ranking, involvement, and breakdown tables),
        // so the number and the badge in the same row can never disagree.
        // Only the one table with no sibling zone column (Worker report's
        // daily history) falls back to classifying the number directly,
        // using the exact thresholds specified for this rule.
        const rowZone = row.zone;
        const tone = typeof rowZone === "string" && rowZone in ZONE_TONE ? ZONE_TONE[rowZone] : classifyPerformanceValue(Number(value));
        return <span className={`nx-report-table__performance-value nx-report-table__performance-value--${tone}`}>{formatted}</span>;
      }
      case "minutes":
        return formatAnalyticsMinutes(String(value), intlLocale);
      case "minutesFraction": {
        // Daily time-accounting cell (task spec §2/§5/§6, Option A): the
        // numerator is this column's own value, the denominator/missing-time
        // signal are sibling fields on the SAME row — the exact convention
        // "percent" already uses for its sibling `row.zone` above. A `null`
        // fund (no Company Performance Settings version existed yet for that
        // date) falls back to the bare total, never a fabricated "/ 0".
        const totalDisplay = formatAnalyticsMinutes(String(value), intlLocale);
        const fund = row.dailyFundMinutes;
        if (fund === null || fund === undefined) return totalDisplay;
        const fundDisplay = formatAnalyticsMinutes(String(fund), intlLocale);
        const missing = row.missingMinutes;
        const missingMinutes = typeof missing === "string" || typeof missing === "number" ? Number(missing) : 0;
        const isIncomplete = missingMinutes > 0;
        // Time-completion coloring is deliberately its own tone (warning),
        // NEVER the performance zone's green/red — the two are different
        // concepts (task spec §13) and must never visually collide.
        return (
          <span className={`nx-report-table__fraction${isIncomplete ? " nx-report-table__fraction--incomplete" : ""}`}>
            {totalDisplay} / {fundDisplay}
            {isIncomplete ? (
              <span className="nx-report-table__fraction-missing">
                {t("dailyTime.missingMinutes", { minutes: missingMinutes })}
              </span>
            ) : null}
          </span>
        );
      }
      case "trend":
        return formatAnalyticsTrend(Number(value));
      case "count":
        return new Intl.NumberFormat(intlLocale).format(Number(value));
      case "date":
        return formatDateSr(String(value));
      case "zone":
        return (
          <Badge tone={ZONE_TONE[String(value)] ?? "neutral"}>{translateDynamic(t, `zone.${value}`, String(value))}</Badge>
        );
      case "status":
        return translateDynamic(t, `status.${value}`, String(value));
      default:
        return String(value);
    }
  }

  if (table.rows.length === 0) return null;

  return (
    <div className="nx-report-panel">
      {showTitle ? <p className="nx-report-panel__title">{translateDynamic(t, table.titleKey, table.titleKey)}</p> : null}
      {/* Horizontal scroll only, not vertical: mirrors Analytics' own
          `.nx-analytics-table-wrap` precedent (`WorkersTable.tsx`) — a wide
          table scrolls inside itself on tablet, the page body never does.
          `nx-scroll-table` (revised —
          docs/NORMAX_PERFORMANCE_REPORTS_ANALYTICS_SCROLL_STICKY_FORENSIC_REPORT.md)
          is a purely visual frame now, NOT a bounded vertical scroll region:
          this table's rows participate directly in the page scroll, and its
          `<thead>` stays sticky against that same page scroll — safe even
          when a section renders several of these tables, since each sticks/
          unsticks independently as its own rows pass through the viewport. */}
      <div className="nx-report-table-wrap nx-scroll-table">
        <table className="nx-report-table">
          <caption className="nx-report-table__caption">{translateDynamic(t, table.titleKey, table.titleKey)}</caption>
          <thead>
            <tr>
              {table.columns.map((col) => (
                <SortableColumnHeader
                  key={col.key}
                  label={translateDynamic(t, col.labelKey, col.labelKey)}
                  sortKey={col.key}
                  activeSortKey={sortKey}
                  direction={direction}
                  onSort={requestSort}
                  className="nx-report-table__center"
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, index) => (
              // Index key is safe here: rows carry no local component state and
              // are fully re-derived from the query response on every render —
              // the same trade-off Analytics' own tables already accept.
              <tr key={index}>
                {table.columns.map((col) => (
                  <td key={col.key} className={alignClassName(col.align)}>
                    {renderCell(row, row[col.key] ?? null, col.format)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
